/**
 * A running account of whether the reel actually played.
 *
 * The reel failing to start is the one bug on this site that leaves no trace:
 * a stopped video looks exactly like its own poster, there is no console error,
 * and `paused` reads false the whole time. It was reported from the field twice
 * before anyone could say what state the element had actually been in, and it
 * could not be reproduced on demand — so the only way to learn anything is to
 * let the visit that hits it carry the evidence out.
 *
 * Read once, at the end of the visit, and attached to that visit's existing
 * traffic event. Nothing new is collected about the person: four numbers about
 * a <video> element, on a record that already exists.
 */

export type ReelStatus = 'ok' | 'stalled' | 'never_started' | 'dismissed' | 'absent';

export interface ReelReport {
  reelStatus: ReelStatus;
  /** furthest point the clock reached, seconds */
  reelMaxTime: number;
  /** readyState last seen: 4 with a maxTime of 0 is the interesting case */
  reelReadyState: number;
  /** how many times the watchdog had to restart it */
  reelKicks: number;
  /** the reel's own length, so maxTime can be read as a fraction of it */
  reelDuration: number;
}

let sawVideo = false;
let maxTime = 0;
let duration = 0;
let readyState = 0;
let kicks = 0;
let dismissed = false;

/** Called on every watchdog tick, which is the only place already polling. */
export const noteReel = (rs: number, t: number, d: number) => {
  sawVideo = true;
  readyState = rs;
  if (t > maxTime) maxTime = t;
  if (Number.isFinite(d) && d > 0) duration = d;
};

export const noteReelKick = () => {
  kicks += 1;
};

/** A visitor who dismissed the reel is not a visitor whose reel broke. */
export const noteReelDismissed = () => {
  dismissed = true;
};

export const reelReport = (): ReelReport => {
  const round = (n: number) => Math.round(n * 100) / 100;
  let reelStatus: ReelStatus;
  if (!sawVideo) reelStatus = 'absent';
  else if (dismissed) reelStatus = 'dismissed';
  // 0.15s rather than 0: a frame or two can be decoded before a stall, and
  // that is still "it never played" to the person looking at it.
  else if (maxTime <= 0.15) reelStatus = 'never_started';
  else if (kicks > 0) reelStatus = 'stalled';
  else reelStatus = 'ok';
  return {
    reelStatus,
    reelMaxTime: Math.min(86400, round(maxTime)),
    reelReadyState: Math.min(4, Math.max(0, Math.round(readyState))),
    reelKicks: Math.min(10000, kicks),
    reelDuration: Math.min(86400, round(duration)),
  };
};
